import {
  createContext,
  type ComponentProps,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Button } from "@/components/ui/button";

export interface FlowPrimaryAction {
  label: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  blockedReason?: string;
  variant?: "default" | "destructive";
  testId?: string;
}

type SetAction = (action: FlowPrimaryAction | null) => void;

const ActionContext = createContext<FlowPrimaryAction | null>(null);
const SetActionContext = createContext<SetAction | null>(null);

export function FlowFooterProvider({ children }: { children: ReactNode }) {
  const [action, setActionState] = useState<FlowPrimaryAction | null>(null);
  const setAction = useCallback<SetAction>((next) => setActionState(next), []);
  return (
    <SetActionContext.Provider value={setAction}>
      <ActionContext.Provider value={action}>{children}</ActionContext.Provider>
    </SetActionContext.Provider>
  );
}

export function useFlowFooterAction(): FlowPrimaryAction | null {
  return useContext(ActionContext);
}

// Hands the node's primary action to the footer. Returns true when a footer
// slot exists, so the node knows to hide its own button; false otherwise.
export function useFlowPrimaryAction(action: FlowPrimaryAction | null): boolean {
  const setAction = useContext(SetActionContext);
  const latest = useRef(action);
  latest.current = action;

  const present = action !== null;
  const label = action?.label;
  const disabled = action?.disabled;
  const blockedReason = action?.blockedReason;
  const variant = action?.variant;
  const testId = action?.testId;

  useLayoutEffect(() => {
    if (!setAction) return;
    if (!present) {
      setAction(null);
      return;
    }
    setAction({
      label,
      disabled,
      blockedReason,
      variant,
      testId,
      onClick: () => latest.current?.onClick(),
    });
  }, [setAction, present, label, disabled, blockedReason, variant, testId]);

  useLayoutEffect(() => {
    if (!setAction) return;
    return () => setAction(null);
  }, [setAction]);

  return setAction !== null;
}

interface FlowPrimaryButtonProps extends FlowPrimaryAction {
  className?: string;
  wrapperClassName?: string;
  size?: ComponentProps<typeof Button>["size"];
}

// For render sites that cannot call a hook directly (inline IIFEs, nodes with
// a single trailing button): hands the action to the footer when there is
// one, otherwise renders the node's own button unchanged.
export function FlowPrimaryButton({
  className,
  wrapperClassName,
  size,
  ...action
}: FlowPrimaryButtonProps) {
  const handedUp = useFlowPrimaryAction(action);
  if (handedUp) return null;
  const button = (
    <Button
      onClick={action.onClick}
      disabled={action.disabled}
      variant={action.variant}
      size={size}
      className={className}
      data-testid={action.testId}
    >
      {action.label}
    </Button>
  );
  return wrapperClassName ? <div className={wrapperClassName}>{button}</div> : button;
}
